import { normalizeId } from '../../utils/ids';
import { PIN_KIND, TILE_SOURCE, getNewestItem, pinTargetOf } from '../../services/favorites';
import { launchQuickAdd } from '../items/QuickAddButtons';
import { launchMultiAdd } from '../items/MultiAddButtons';
import { newItemPath } from '../items/FavoriteCardButtons';
import { dashboardPath } from '../../utils/dashboards';
import { combinedListPath } from '../../utils/combinedLists';
import { savedFilterPath } from '../../utils/savedFilters';
import { isItemTag, isPrivateTag, tagPagePath, tagRecordOf } from '../../utils/tags';
import { FUNNEL, LAYERS } from '../icons';

/**
 * What a resolved Home pin (GET /api/home) reads as, and where it goes —
 * shared by the Home page and the command palette so the two never word or
 * launch a pin differently. Every kind on the ONE Pinned strip (KPR-1123) —
 * launchers, records, tags, combined lists, saved filters, the newest record
 * of a card, a record tile (KPR-1127), a list of records (KPR-1128) — answers the same four questions: `pinLabel` (its name),
 * `pinKind` (the word above it), `pinDescription` (one line: what it does, and
 * where) and `openPin` (go).
 */

const cardWord = card => card?.label?.singular || card?.name || 'item';

/** The launcher's own name: the template's or form's, or the card's. */
export function launcherTitle(pin) {
	if (pin?.targetType === PIN_KIND.QUICK_ADD) return pin.template?.name || '(untitled quick add)';
	if (pin?.targetType === PIN_KIND.MULTI_ADD) return pin.form?.name || '(untitled multi add)';
	if (pin?.targetType === PIN_KIND.DASHBOARD) return pin.dashboard?.name || 'Untitled dashboard';
	return pin?.card?.name || '(untitled card)';
}

/** The kind of launcher, in the product's words. */
export function launcherKind(pin) {
	if (pin?.targetType === PIN_KIND.QUICK_ADD) return 'Quick add';
	if (pin?.targetType === PIN_KIND.MULTI_ADD) return 'Multi add';
	if (pin?.targetType === PIN_KIND.DASHBOARD) return 'Dashboard';
	return 'Add item';
}

/** One line for a tooltip or a palette row: what launching it does, and where. */
export function launcherDescription(pin) {
	const where = pin?.collection?.name || 'a collection';
	if (pin?.targetType === PIN_KIND.QUICK_ADD) return `Quick add · ${launcherTitle(pin)} in ${where}`;
	if (pin?.targetType === PIN_KIND.MULTI_ADD) {
		const cards = (pin.cards || []).map(card => card?.name).filter(Boolean).join(', ');
		return `Multi add · ${launcherTitle(pin)}${cards ? ` (${cards})` : ''} in ${where}`;
	}
	if (pin?.targetType === PIN_KIND.DASHBOARD) return `Dashboard · ${launcherTitle(pin)} in ${where}`;
	return `Add ${cardWord(pin?.card)} to ${where}`;
}

/**
 * Launches exactly as the collection home does: the new-item form for an
 * add-item card, the template-resolved form for a quick add, the multi add
 * page for a form — and a dashboard launcher (charts A7) opens the dashboard
 * in its context, the collection whose items it counts.
 */
export function launchPin(navigate, pin) {
	const collectionId = normalizeId(pin?.collection_id);
	if (!collectionId) return;
	if (pin.targetType === PIN_KIND.QUICK_ADD) {
		launchQuickAdd(navigate, collectionId, pin.template || { _id: pin.target_id });
		return;
	}
	if (pin.targetType === PIN_KIND.MULTI_ADD) {
		launchMultiAdd(navigate, collectionId, pin.form || { _id: pin.target_id });
		return;
	}
	if (pin.targetType === PIN_KIND.DASHBOARD) {
		navigate(dashboardPath(collectionId, normalizeId(pin.dashboard?._id || pin.target_id)));
		return;
	}
	navigate(newItemPath(collectionId, pin.target_id));
}

/**
 * Where a pinned record opens: the list of the collection the item lives in
 * NOW, with its detail. The item's own `collection_id` wins over the pin's —
 * a record moves with its id (a global collection retiring hands its records
 * to the keepr archive), and a pin row once froze where it was pinned, which
 * opened a list the record was not in, or a collection the person could no
 * longer read. GET /api/home serves both as the current collection today;
 * the order here is the guard if the two ever disagree.
 */
export function recordPath(pin) {
	const collectionId = normalizeId(pin?.item?.collection_id || pin?.collection_id);
	const itemId = normalizeId(pin?.item?._id || pin?.target_id);
	return `/collections/${collectionId}/items?item=${itemId}`;
}

/**
 * The newest record of a card (KPR-1126): "Newest Note", or under a saved
 * filter the filter's name with "Newest Note" above it. Which record it opens
 * is asked when it is clicked (openNewestRecord), never when it was pinned.
 */
export const isNewestPin = pin => pin?.targetType === PIN_KIND.LATEST_RECORD;
const newestOf = pin => `Newest ${cardWord(pin?.card)}`;
const newestFilterName = pin => (typeof pin?.savedFilter?.name === 'string' ? pin.savedFilter.name.trim() : '');

/** The one line a newest-record pin says when nothing matches yet. */
export function newestEmptyText(pin) {
	const filterName = newestFilterName(pin);
	return filterName ? `No ${cardWord(pin?.card)} in “${filterName}” yet.` : `No ${cardWord(pin?.card)} yet.`;
}

/**
 * Opens the newest record a newest-record pin names, asked NOW: the item's
 * detail in the list of the collection it lives in. When none matches,
 * `notify.empty({ text, addPath })` says so in one line — `addPath` the
 * new-item form where the card lives, only when the person may add one;
 * a refusal is `notify.error(text)`. Resolves when it has done one of them.
 */
export async function openNewestRecord(navigate, pin, notify = {}) {
	const { targetId: cardId, collectionId, savedFilterId } = pinTargetOf(pin);
	if (!cardId || !collectionId) return;
	try {
		const answer = await getNewestItem({ collectionId, cardId, savedFilterId });
		if (answer.item) {
			navigate(recordPath({ item: answer.item }));
			return;
		}
		notify.empty?.({ text: newestEmptyText(pin), addPath: answer.canAdd ? newItemPath(answer.addCollectionId, cardId) : null });
	} catch (err) {
		notify.error?.(err?.response?.data?.message || 'Could not open the newest item. Please try again.');
	}
}

/**
 * A record tile (KPR-1127): one to three values of a record — a fixed one,
 * or the newest record of a card. Home reads the record when it loads, so
 * the tile opens the record it shows (`item`); a newest tile with nothing
 * matching has none, and says so in one line.
 */
export const isTilePin = pin => pin?.targetType === PIN_KIND.RECORD_TILE;
export const isLatestTile = pin => isTilePin(pin) && pin.source === TILE_SOURCE.LATEST;

/** A tile's label: its record's title, or "Newest <card>" for the newest record of a card. */
export function tileLabel(pin) {
	if (isLatestTile(pin)) return newestOf(pin);
	const title = typeof pin?.item?.displayValue === 'string' ? pin.item.displayValue.trim() : '';
	return title || 'Untitled item';
}

/**
 * The small line over a tile's label: a fixed record's card ("Reading"); for
 * the newest record, the saved filter it is under, else its collection —
 * "Newest Reading" alone does not say where.
 */
export function tileKicker(pin) {
	if (isLatestTile(pin)) return newestFilterName(pin) || pin?.collection?.name || 'Tile';
	return pin?.card?.label?.singular || pin?.card?.name || 'Tile';
}

/**
 * A short list of a card's records (KPR-1128): the first few records of a
 * card the person reads — newest first, or under a saved filter in its own
 * order — read by Home each time it loads. Its heading opens the whole list,
 * each row its record.
 */
export const isRecordListPin = pin => pin?.targetType === PIN_KIND.RECORD_LIST;
const cardPlural = card => card?.label?.plural || card?.name || 'Items';

/** A list's name: its saved filter's, else its card's in the plural ("Readings"). */
export function recordListLabel(pin) {
	return newestFilterName(pin) || cardPlural(pin?.card);
}

/** The small line over a list's name: its card when a filter names it, else its collection. */
export function recordListKicker(pin) {
	if (newestFilterName(pin)) return pin?.card?.name || 'List';
	return pin?.collection?.name || 'List';
}

/**
 * The whole list a list of records shows the start of: the collection's List
 * of its card, under its filter as it reads now (KPR-1124's link, every part
 * written so the person's own stored sort does not outvote it), in the order
 * the list reads — the filter's, else created date, newest first.
 */
export function recordListPath(pin) {
	const collectionId = normalizeId(pin?.collection_id || pin?.collection?._id);
	const cardId = normalizeId(pin?.card_id || pin?.card?._id);
	if (!collectionId) return null;
	const filter = pin?.savedFilter && typeof pin.savedFilter === 'object' ? pin.savedFilter : {};
	return savedFilterPath(collectionId, {
		...filter,
		sort_field: filter.sort_field || 'createdAt',
		sort_direction: filter.sort_field ? (filter.sort_direction || 'desc') : 'desc',
		card: cardId || null,
	});
}

/** The one line a list says when nothing matches. */
export const RECORD_LIST_EMPTY_TEXT = 'No records yet';

/** A pinned record's title: the server's stored display value. */
export function recordTitle(pin) {
	const title = typeof pin?.item?.displayValue === 'string' ? pin.item.displayValue.trim() : '';
	return title || 'Untitled item';
}

const LAUNCHER_KINDS = [PIN_KIND.ADD_ITEM, PIN_KIND.QUICK_ADD, PIN_KIND.MULTI_ADD, PIN_KIND.DASHBOARD];

/** A launcher — it starts something (a form, a dashboard) rather than opening a page. */
export const isLauncherPin = pin => LAUNCHER_KINDS.includes(pin?.targetType);

/** What a pinned tag is called: an item tag its item's title, any other its name. */
export const pinnedTagTitle = pin => (isItemTag(pin?.tag) ? (pin.tag.title || 'Untitled item') : (pin?.tag?.name || 'Tag'));

/** The kind of tag, in the words the product uses for each (ruled d01). */
const tagKind = tag => {
	if (isItemTag(tag)) return 'Item tag';
	if (isPrivateTag(tag)) return 'Private tag';
	return 'Collection tag';
};

/** A pinned collection (KPR-1125) — and its parent's name when it is a sub-collection. */
const isCollectionLink = pin => pin?.targetType === PIN_KIND.COLLECTION_LINK;
const collectionName = pin => pin?.collection?.name || 'Untitled collection';
const parentName = pin => (isCollectionLink(pin) ? pin.parent?.name || null : null);

/** A pin's own name, the cell's title. */
export function pinLabel(pin) {
	if (isLauncherPin(pin)) return launcherTitle(pin);
	if (pin?.targetType === PIN_KIND.ITEM) return recordTitle(pin);
	if (pin?.targetType === PIN_KIND.TAG) return pinnedTagTitle(pin);
	if (pin?.targetType === PIN_KIND.COMBINED_LIST) return pin.combinedList?.name || 'Combined list';
	if (isNewestPin(pin)) return newestFilterName(pin) || newestOf(pin);
	// The filter's name as Home read it (an edit or a rename follows with no re-pin).
	if (pin?.targetType === PIN_KIND.SAVED_FILTER) return pin.savedFilter?.name || 'Untitled filter';
	if (isTilePin(pin)) return tileLabel(pin);
	if (isCollectionLink(pin)) return collectionName(pin);
	if (isRecordListPin(pin)) return recordListLabel(pin);
	return 'Pin';
}

/**
 * What a pin's cell draws as its title, under pinKind: its name — except an
 * unfiltered newest-record pin, whose cell reads **Newest** over its card
 * ("Newest" / "Reading"), so the card's word is never the part a narrow cell
 * cuts off. Everywhere else (its menu, its palette row, its accessible
 * name) it is pinLabel's "Newest Reading".
 */
export function pinTitle(pin) {
	if (isNewestPin(pin) && !newestFilterName(pin)) return cardWord(pin?.card);
	return pinLabel(pin);
}

/** The word above a pin's name: what kind of thing it is (a record: its card). */
export function pinKind(pin) {
	if (isLauncherPin(pin)) return launcherKind(pin);
	if (pin?.targetType === PIN_KIND.ITEM) return pin.card?.label?.singular || pin.card?.name || 'Item';
	if (pin?.targetType === PIN_KIND.TAG) return tagKind(pin.tag);
	if (pin?.targetType === PIN_KIND.COMBINED_LIST) return 'Combined list';
	// "Newest" over "Reading" (pinTitle); "Newest Reading" over a filter's name.
	if (isNewestPin(pin)) return newestFilterName(pin) ? newestOf(pin) : 'Newest';
	if (pin?.targetType === PIN_KIND.SAVED_FILTER) return 'Filter';
	if (isTilePin(pin)) return 'Tile';
	// A sub-collection reads "Parent › Child": the parent above its name.
	if (isCollectionLink(pin)) return parentName(pin) ? `${parentName(pin)} ›` : 'Collection';
	if (isRecordListPin(pin)) return 'List';
	return '';
}

/**
 * The small line over a pin's name in its compact cell. It is the pin's kind
 * ("Quick add", a record's card) — except for a saved filter, whose name
 * alone ("Recent", "This week") says nothing without the collection it
 * filters, so the cell shows that: the filter's name AND its collection.
 */
export function pinKicker(pin) {
	if (pin?.targetType === PIN_KIND.SAVED_FILTER) return pin.collection?.name || pinKind(pin);
	if (isTilePin(pin)) return tileKicker(pin);
	if (isRecordListPin(pin)) return recordListKicker(pin);
	return pinKind(pin);
}

/**
 * Where a pin lives, by name, for its line: its collection — a tag's only
 * where the person can see it (GET /api/home sends `collection` null
 * otherwise), a combined list's never (it belongs to none).
 */
export const pinWhere = pin => (pin?.targetType === PIN_KIND.COMBINED_LIST || isCollectionLink(pin) ? null : (pin?.collection?.name || null));

/** One line for a tooltip or a palette row: what the pin is, and where. */
export function pinDescription(pin) {
	if (isLauncherPin(pin)) return launcherDescription(pin);
	if (isCollectionLink(pin)) return `${parentName(pin) ? 'Sub-collection' : 'Collection'} · ${parentName(pin) ? `${parentName(pin)} › ` : ''}${collectionName(pin)}`;
	if (isNewestPin(pin)) {
		const where = pinWhere(pin);
		const filterName = newestFilterName(pin);
		return `${newestOf(pin)}${filterName ? ` · ${filterName}` : ''}${where ? ` in ${where}` : ''}`;
	}
	if (isLatestTile(pin)) {
		const where = pinWhere(pin);
		const filterName = newestFilterName(pin);
		return `Tile · ${newestOf(pin)}${filterName ? ` · ${filterName}` : ''}${where ? ` in ${where}` : ''}`;
	}
	const where = pinWhere(pin);
	return `${pinKind(pin)} · ${pinLabel(pin)}${where ? ` in ${where}` : ''}`;
}

/**
 * What a screen reader hears for a pin's cell — record first, as a qualified
 * badge is named ("Molly · 8am, Medication, in Biewend"): the compact cell
 * draws no collection segment, so the words carry it.
 */
export function pinAccessibleName(pin) {
	if (isCollectionLink(pin)) return parentName(pin) ? `${collectionName(pin)}, sub-collection of ${parentName(pin)}` : `${collectionName(pin)}, collection`;
	const where = pinWhere(pin);
	if (isNewestPin(pin)) {
		const filterName = newestFilterName(pin);
		return `${newestOf(pin)}${filterName ? `, ${filterName}` : ''}${where ? `, in ${where}` : ''}`;
	}
	if (isLatestTile(pin)) {
		const filterName = newestFilterName(pin);
		return `${newestOf(pin)}${filterName ? `, ${filterName}` : ''}, tile${where ? `, in ${where}` : ''}`;
	}
	if (isTilePin(pin)) return `${tileLabel(pin)}, tile${where ? `, in ${where}` : ''}`;
	if (isRecordListPin(pin)) {
		const filterName = newestFilterName(pin);
		return `${recordListLabel(pin)}, list${filterName ? ` of ${cardPlural(pin?.card)}` : ''}${where ? `, in ${where}` : ''}`;
	}
	return `${pinLabel(pin)}, ${pinKind(pin)}${where ? `, in ${where}` : ''}`;
}

/** Where a pin that opens a page goes; null for a launcher or a newest-record pin (openPin asks first). */
export function pinPath(pin) {
	// The collection's home, as the Collections strip links it.
	if (isCollectionLink(pin)) {
		const id = normalizeId(pin.collection?._id || pin.target_id);
		return id ? `/collections/${id}` : null;
	}
	if (pin?.targetType === PIN_KIND.ITEM) return recordPath(pin);
	if (pin?.targetType === PIN_KIND.TAG) return tagPagePath(pin.tag);
	if (pin?.targetType === PIN_KIND.COMBINED_LIST) {
		const id = normalizeId(pin.combinedList?._id || pin.target_id);
		return id ? combinedListPath(id) : null;
	}
	// The collection's List with the filter as it reads NOW (KPR-1124).
	if (pin?.targetType === PIN_KIND.SAVED_FILTER) return savedFilterPath(pin.collection_id || pin.collection?._id, pin.savedFilter);
	// A tile opens the record it shows — for the newest record, the one Home
	// read when it loaded; none when nothing matches yet.
	if (isTilePin(pin)) return pin.item?._id ? recordPath({ item: pin.item }) : null;
	// A list of records: the whole list (each row opens its own record).
	if (isRecordListPin(pin)) return recordListPath(pin);
	return null;
}

/**
 * Opens or launches any pin, exactly as its cell on Home does. A
 * newest-record pin asks which record first (`notify`: openNewestRecord's).
 */
export function openPin(navigate, pin, notify = {}) {
	if (isNewestPin(pin)) return openNewestRecord(navigate, pin, notify);
	// A newest tile with nothing matching says so (the palette's row; the
	// tile itself shows the line), with no add link: a tile reads, it does
	// not add.
	if (isTilePin(pin) && !pin.item?._id) {
		if (pin.unavailable) {
			notify.error?.('Could not read this just now. Reload Home to try again.');
			return Promise.resolve();
		}
		notify.empty?.({ text: newestEmptyText(pin), addPath: null });
		return Promise.resolve();
	}
	if (isLauncherPin(pin)) {
		launchPin(navigate, pin);
	} else {
		const path = pinPath(pin);
		if (path) navigate(path);
	}
	return Promise.resolve();
}

/**
 * What a pin's cell draws — the record badge's parts: `kind` (the tile's
 * fallback glyph and role), `record` (the icon on its color) or `records` (a
 * multi add's stack), `fallbackIcon` where the kind has none of its own.
 */
export function pinBadge(pin) {
	if (isCollectionLink(pin)) return { kind: 'collection', record: pin.collection || null };
	if (pin?.targetType === PIN_KIND.MULTI_ADD) return { kind: 'card', records: pin.cards || [] };
	if (pin?.targetType === PIN_KIND.DASHBOARD) return { kind: 'dashboard', record: null };
	if (pin?.targetType === PIN_KIND.ITEM) return { kind: 'item', record: pin.card || null };
	if (pin?.targetType === PIN_KIND.TAG) {
		return isItemTag(pin.tag) ? { kind: 'item', record: pin.card || null } : { kind: 'tag', record: tagRecordOf(pin.tag) };
	}
	if (pin?.targetType === PIN_KIND.COMBINED_LIST) return { kind: 'card', record: { name: pinLabel(pin), icon: null, color: null }, fallbackIcon: LAYERS };
	// The card's own tile: the newest of its records.
	if (isNewestPin(pin)) return { kind: 'card', record: pin.card || null };
	// A funnel on the collection's own color: the filter belongs to it.
	if (pin?.targetType === PIN_KIND.SAVED_FILTER) return { kind: 'card', record: { name: pinLabel(pin), icon: null, color: pin.collection?.color || null }, fallbackIcon: FUNNEL };
	// A tile wears its record's card (a newest tile, the card it was pinned for).
	if (isTilePin(pin)) return { kind: isLatestTile(pin) ? 'card' : 'item', record: pin.card || null };
	// A list wears the card whose records it lists.
	if (isRecordListPin(pin)) return { kind: 'card', record: pin.card || null };
	return { kind: 'card', record: pin?.card || null };
}
